import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { HighlightedText, Tooltip } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { getConversationSessionId, useChatContext } from '@/contexts/ChatContext';
import { useTenantUsers } from '@/hooks/useTasks';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { PersonAvatar } from '@/components/common/PersonAvatar';
import { ChatActionsMenu } from '@/components/chats/ChatActionsMenu';
import { ChatStatusDot } from '@/components/chats/ChatStatusDot';
import { titleOf } from '@/components/chats/chatTitle';

export interface ChatRowProps {
  execution: WorkflowExecution;
  workflowInstanceId: string;
  isActive: boolean;
  /** The live search term, so the matched part of the title is marked. */
  search?: string;
  onRename: () => void;
  onDelete: () => void;
  onSetPinned: (pinned: boolean) => void;
}

/** One conversation. Rename, delete and pin surface as callbacks so the row can still be rendered in a test without a QueryClient. */
export function ChatRow({
  execution,
  workflowInstanceId,
  isActive,
  search,
  onRename,
  onDelete,
  onSetPinned,
}: ChatRowProps) {
  const title = titleOf(execution);
  const isPinned = Boolean(execution.pinned_at);

  const { sessions } = useChatContext();
  const sessionId = getConversationSessionId(workflowInstanceId, String(execution.id));
  const busy = Boolean(sessions[sessionId]?.isLoading);

  const { data: tenantUsers } = useTenantUsers();
  const owner = execution.user_id
    ? (tenantUsers?.find(user => user.id === execution.user_id) ?? null)
    : null;

  /** Only a truncated title gets a tooltip; the rest would only repeat what the row already reads. */
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const node = titleRef.current;
    if (!node) return;

    const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [title]);

  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: String(execution.id),
    data: { pinned: isPinned },
  });

  return (
    <li
      ref={setNodeRef}
      {...listeners}
      className={cn(
        'group relative flex h-[26px] items-center gap-0.5 rounded-v2-lg pr-1 text-[13px] leading-[18px]',
        'text-v2-sidebar-foreground/[0.78] transition-colors duration-v2-quick',
        'hover:bg-v2-sidebar-accent has-[:focus-visible]:bg-v2-sidebar-accent',
        isActive && 'bg-v2-sidebar-active font-medium text-v2-sidebar-accent-foreground',
        isDragging && 'opacity-40',
      )}
    >
      <Link
        to={`/ui/chat/${execution.id}`}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-0.5 rounded-v2-lg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
        )}
      >
        {/* Near-black on the active row, 56% elsewhere — with no background tint on the active row, this carries it. */}
        <span
          className={cn(
            'grid h-[26px] w-7 shrink-0 place-items-center',
            isActive
              ? 'text-v2-sidebar-accent-foreground'
              : 'text-v2-sidebar-foreground/[0.56]',
          )}
        >
          <ChatStatusDot busy={busy} />
        </span>
        <Tooltip
          label={title}
          side="right"
          disabled={!isTruncated}
          triggerClassName="min-w-0 flex-1"
        >
          <span ref={titleRef} className="block w-full truncate">
            <HighlightedText text={title} query={search} />
          </span>
        </Tooltip>
      </Link>
      {/* Sized to the tallest thing it holds, the menu trigger, so the avatar sits on the row's own centre. */}
      <span className="relative grid h-6 w-5 shrink-0 place-items-center">
        {owner ? (
          <span
            role="img"
            aria-label={`Started by ${owner.display_name}`}
            className={cn(
              'col-start-1 row-start-1 transition-opacity duration-v2-quick',
              'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0 [@media(pointer:coarse)]:opacity-0',
            )}
          >
            <PersonAvatar person={owner} title={owner.display_name} className="size-4" />
          </span>
        ) : null}
        <span className="col-start-1 row-start-1">
          <ChatActionsMenu
            chatTitle={title}
            pinned={isPinned}
            onPin={() => onSetPinned(!isPinned)}
            onRename={onRename}
            onDelete={onDelete}
          />
        </span>
      </span>
    </li>
  );
}
